/**
 * AppShell — layout for all non-console pages.
 *
 * Left nav rail (functional), a slim top bar (page title + project
 * selector + account menu), then the scrollable content column.
 *
 * The Founder Console keeps its own bespoke layout (frozen in Phase 0)
 * and is NOT wrapped by this shell.
 */
import { Outlet } from "react-router-dom";
import NavRail from "@/components/NavRail";
import AppTopBar from "@/components/AppTopBar";
import BackgroundField from "@/components/BackgroundField";
import QorynCommandPalette from "@/components/QorynCommandPalette";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";

export default function AppShell() {
    const [paletteOpen, setPaletteOpen] = useState(false);
    const { user, signOut } = useAuth();

    useEffect(() => {
        const onKey = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const handleSignOut = useCallback(async () => {
        await signOut();
    }, [signOut]);

    return (
        <div
            data-testid="app-shell"
            className="relative min-h-screen w-full bg-qoryn-bg text-qoryn-text flex"
        >
            <BackgroundField />
            <NavRail onOpenLauncher={() => setPaletteOpen(true)} />
            <div className="flex-1 min-w-0 min-h-screen flex flex-col relative z-10">
                <AppTopBar />
                <main
                    data-testid="app-main"
                    className="flex-1 min-w-0 overflow-y-auto qoryn-transcript-scroll"
                >
                    <div className="max-w-6xl mx-auto w-full px-6 md:px-10 py-8">
                        <Outlet />
                    </div>
                </main>
            </div>

            {/* Command palette (shell context — no live session, so most
                session-scoped rows are visibly disabled; sign-out works). */}
            <QorynCommandPalette
                open={paletteOpen}
                onClose={() => setPaletteOpen(false)}
                turns={[]}
                sessionStartedAt={null}
                diagnostics={[]}
                connectionState="closed"
                voices={[]}
                previewable={[]}
                currentVoice={null}
                voicePref={null}
                onSetVoicePref={() => {}}
                onExportTranscript={() => {}}
                onCopyTranscript={async () => false}
                onClearTranscript={() => {}}
                onDiagnosticsSnapshot={() => {}}
                onStartSession={() => { window.location.href = "/"; }}
                onRestartSessionWithVoice={() => {}}
                authEmail={user?.email ?? null}
                onSignOut={handleSignOut}
            />
        </div>
    );
}
